import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Save, Send } from 'lucide-react';
import { PageHeader } from '../../components/layout';
import {
    Card, CardBody, Button, Input, TextArea, Select,
    DatePicker, Radio, toast
} from '../../components/common';
import { purchaseRequestApi, catalogApi, budgetApi, departmentApi } from '../../services/api';
import CatalogItemPicker from './CatalogItemPicker';
import './PurchaseRequestForm.css';

export default function PurchaseRequestForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const queryClient = useQueryClient();
    const isEditing = !!id;

    const [form, setForm] = useState({
        title: '',
        description: '',
        departmentId: '',
        budgetId: '',
        priority: 'medium',
        requiredDate: '',
        items: []
    });
    const [errors, setErrors] = useState({});
    const [showItemPicker, setShowItemPicker] = useState(false);

    // Fetch existing PR if editing
    const { data: prData } = useQuery({
        queryKey: ['purchaseRequest', id],
        queryFn: () => purchaseRequestApi.get(id),
        enabled: isEditing,
        select: (res) => res.data?.data
    });

    // Fetch departments from API
    const { data: departments = [] } = useQuery({
        queryKey: ['departments'],
        queryFn: async () => {
            try {
                const res = await departmentApi.list();
                return res.data?.data || res.data || [];
            } catch {
                return [];
            }
        }
    });

    // Fetch budgets
    const { data: budgets } = useQuery({
        queryKey: ['budgets', 'active'],
        queryFn: () => budgetApi.list({ status: 'active' }),
        select: (res) => res.data?.data || []
    });

    useEffect(() => {
        if (prData) {
            setForm({
                title: prData.title || '',
                description: prData.description || '',
                departmentId: prData.departmentId || '',
                budgetId: prData.budgetId || '',
                priority: prData.priority || 'medium',
                requiredDate: prData.requiredDate?.split('T')[0] || '',
                items: prData.lines?.map(line => ({
                    catalogItemId: line.itemId || line.catalogItemId,
                    itemCode: line.itemCode,
                    itemName: line.itemName,
                    description: line.specifications || line.description || '',
                    quantity: line.quantity || 1,
                    unitPrice: line.estimatedPrice || line.unitPrice || 0,
                    uom: line.uomCode || 'PCS'
                })) || []
            });
        }
    }, [prData]);

    // Create/Update mutation
    const saveMutation = useMutation({
        mutationFn: (data) => isEditing
            ? purchaseRequestApi.update(id, data)
            : purchaseRequestApi.create(data),
        onSuccess: (res) => {
            toast.success(`Purchase Request ${isEditing ? 'updated' : 'created'} successfully`);
            queryClient.invalidateQueries(['purchaseRequests']);
            navigate(`/purchase-requests/${res.data?.data?.id || id}`);
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Failed to save');
        }
    });

    // Submit mutation
    const submitMutation = useMutation({
        mutationFn: (prId) => purchaseRequestApi.submit(prId),
        onSuccess: () => {
            toast.success('Purchase Request submitted for approval');
            queryClient.invalidateQueries(['purchaseRequests']);
            navigate('/purchase-requests');
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Failed to submit');
        }
    });

    const handleChange = (e) => {
        const { name, value } = e.target;

        // Reset budgetId when department changes
        if (name === 'departmentId') {
            // Find budget for this department
            const deptBudget = budgets?.find(b => b.departmentId === value);
            setForm(prev => ({
                ...prev,
                [name]: value,
                budgetId: deptBudget?.id || '' // Auto-select if available
            }));
        } else {
            setForm(prev => ({ ...prev, [name]: value }));
        }
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleAddItem = (catalogItem) => {
        const newItem = {
            catalogItemId: catalogItem.id,
            catalogItem,
            itemName: catalogItem.itemName,
            itemCode: catalogItem.itemCode,
            description: '',
            quantity: 1,
            unitPrice: catalogItem.standardPrice || catalogItem.unitPrice || 0,
            uom: catalogItem.primaryUom?.code || 'PCS'
        };
        setForm(prev => ({
            ...prev,
            items: [...prev.items, newItem]
        }));
        setShowItemPicker(false);
    };

    const handleUpdateItem = (index, field, value) => {
        setForm(prev => ({
            ...prev,
            items: prev.items.map((item, i) =>
                i === index ? { ...item, [field]: value } : item
            )
        }));
    };

    const handleRemoveItem = (index) => {
        setForm(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };

    const calculateTotal = () => {
        return form.items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
    };

    const validate = () => {
        const newErrors = {};
        if (!form.title) newErrors.title = 'Title is required';
        if (!form.departmentId) newErrors.departmentId = 'Department is required';
        if (form.items.length === 0) newErrors.items = 'At least one item is required';
        setErrors(newErrors);

        if (Object.keys(newErrors).length > 0) {
            const errorMessages = Object.values(newErrors).join(', ');
            toast.error(`Please fix the following errors: ${errorMessages}`);
            return false;
        }
        return true;
    };

    const handleSave = () => {
        if (!validate()) return;

        // Format required date properly
        const requiredDate = form.requiredDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

        saveMutation.mutate({
            title: form.title,
            description: form.description || '',
            departmentId: form.departmentId, // Keep as UUID string
            budgetPlanId: form.budgetId || undefined, // Only include if set
            priority: form.priority || 'medium',
            requiredDate: requiredDate,
            lines: form.items.map((item, index) => ({
                lineNumber: index + 1,
                itemId: item.catalogItemId,
                itemCode: item.itemCode,
                itemName: item.itemName,
                specifications: item.description || '',
                quantity: parseInt(item.quantity) || 1,
                estimatedPrice: parseFloat(item.unitPrice) || 0,
                uomCode: item.uom || 'PCS'
            }))
        });
    };

    const handleSaveAndSubmit = async () => {
        if (!validate()) return;

        // Format required date properly
        const requiredDate = form.requiredDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

        const saveResult = await saveMutation.mutateAsync({
            title: form.title,
            description: form.description || '',
            departmentId: form.departmentId, // Keep as UUID string
            budgetPlanId: form.budgetId || undefined, // Only include if set
            priority: form.priority || 'medium',
            requiredDate: requiredDate,
            lines: form.items.map((item, index) => ({
                lineNumber: index + 1,
                itemId: item.catalogItemId,
                itemCode: item.itemCode,
                itemName: item.itemName,
                specifications: item.description || '',
                quantity: parseInt(item.quantity) || 1,
                estimatedPrice: parseFloat(item.unitPrice) || 0,
                uomCode: item.uom || 'PCS'
            }))
        });

        const prId = saveResult.data?.data?.id || id;
        if (prId) {
            submitMutation.mutate(prId);
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0
        }).format(value || 0);
    };

    const deptOptions = departments?.map(d => ({ value: d.id, label: d.name })) || [];

    // Filter budgets based on selected department
    const filteredBudgets = form.departmentId
        ? budgets?.filter(b => b.departmentId === form.departmentId)
        : budgets;

    const budgetOptions = [
        { value: '', label: 'No budget allocation' },
        ...(filteredBudgets?.map(b => ({
            value: b.id,
            label: `${b.budgetCode} - ${formatCurrency(b.totalAmount - (b.committedAmount || 0) - (b.spentAmount || 0))} available`
        })) || [])
    ];

    return (
        <div className="pr-form-page">
            <PageHeader
                title={isEditing ? 'Edit Purchase Request' : 'Create Purchase Request'}
                breadcrumbs={[
                    { label: 'Purchase Requests', path: '/purchase-requests' },
                    { label: isEditing ? 'Edit' : 'Create' }
                ]}
            />

            <div className="pr-form-content">
                <Card className="pr-form-card">
                    <CardBody>
                        <h3>Request Details</h3>
                        <div className="form-grid">
                            <div className="form-full">
                                <Input
                                    name="title"
                                    label="Request Title"
                                    placeholder="Enter a descriptive title"
                                    value={form.title}
                                    onChange={handleChange}
                                    error={errors.title}
                                    required
                                />
                            </div>
                            <Select
                                name="departmentId"
                                label="Department"
                                value={form.departmentId}
                                onChange={handleChange}
                                options={deptOptions}
                                error={errors.departmentId}
                                required
                            />
                            <Select
                                name="budgetId"
                                label="Budget Allocation"
                                value={form.budgetId}
                                onChange={handleChange}
                                options={budgetOptions}
                            />
                            <DatePicker
                                label="Required Date"
                                value={form.requiredDate}
                                onChange={(date) => setForm(prev => ({ ...prev, requiredDate: date }))}
                                minDate={new Date().toISOString().split('T')[0]}
                            />
                            <Radio
                                name="priority"
                                label="Priority"
                                value={form.priority}
                                onChange={(val) => setForm(prev => ({ ...prev, priority: val }))}
                                options={[
                                    { value: 'low', label: 'Low' },
                                    { value: 'medium', label: 'Medium' },
                                    { value: 'high', label: 'High' },
                                    { value: 'urgent', label: 'Urgent' }
                                ]}
                                direction="horizontal"
                            />
                            <div className="form-full">
                                <TextArea
                                    name="description"
                                    label="Description"
                                    placeholder="Additional details or notes"
                                    value={form.description}
                                    onChange={handleChange}
                                    rows={3}
                                />
                            </div>
                        </div>
                    </CardBody>
                </Card>

                <Card className="pr-items-card">
                    <CardBody>
                        <div className="items-header">
                            <h3>Line Items</h3>
                            <Button icon={Plus} size="sm" onClick={() => setShowItemPicker(true)}>
                                Add Item
                            </Button>
                        </div>

                        {errors.items && <p className="items-error">{errors.items}</p>}

                        {form.items.length === 0 ? (
                            <div className="items-empty">
                                <p>No items added yet. Click "Add Item" to select from catalog.</p>
                            </div>
                        ) : (
                            <div className="items-table">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Item</th>
                                            <th>Description</th>
                                            <th>Qty</th>
                                            <th>UOM</th>
                                            <th>Unit Price</th>
                                            <th>Total</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {form.items.map((item, index) => (
                                            <tr key={index}>
                                                <td>
                                                    <div className="item-info">
                                                        <span className="item-code">{item.itemCode}</span>
                                                        <span className="item-name">{item.itemName}</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={item.description}
                                                        onChange={(e) => handleUpdateItem(index, 'description', e.target.value)}
                                                        placeholder="Notes..."
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={item.quantity}
                                                        onChange={(e) => handleUpdateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                                                        className="qty-input"
                                                    />
                                                </td>
                                                <td>{item.uom}</td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        value={item.unitPrice}
                                                        onChange={(e) => handleUpdateItem(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                                                        className="price-input"
                                                    />
                                                </td>
                                                <td className="total-cell">{formatCurrency(item.quantity * item.unitPrice)}</td>
                                                <td>
                                                    <button
                                                        className="remove-btn"
                                                        onClick={() => handleRemoveItem(index)}
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td colSpan="5" className="grand-total-label">Grand Total</td>
                                            <td className="grand-total-value">{formatCurrency(calculateTotal())}</td>
                                            <td></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        )}
                    </CardBody>
                </Card>

                <div className="pr-form-actions">
                    <Button variant="secondary" onClick={() => navigate('/purchase-requests')}>
                        Cancel
                    </Button>
                    <Button
                        variant="secondary"
                        icon={Save}
                        onClick={handleSave}
                        loading={saveMutation.isPending}
                    >
                        Save as Draft
                    </Button>
                    <Button
                        icon={Send}
                        onClick={handleSaveAndSubmit}
                        loading={saveMutation.isPending || submitMutation.isPending}
                    >
                        Save & Submit
                    </Button>
                </div>
            </div>

            <CatalogItemPicker
                isOpen={showItemPicker}
                onClose={() => setShowItemPicker(false)}
                onSelect={handleAddItem}
                excludeIds={form.items.map(i => i.catalogItemId)}
            />
        </div>
    );
}
